import { create } from "zustand";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";

/** One historical revision of a GeneratedDocument. Mirror of GeneratedDocumentVersionDto. */
export type DocVersion = {
  versionId: string;
  versionNumber: number;
  /** True iff a rendered LDOM diff HTML is stored alongside this version. v1 is always false. */
  hasDiff: boolean;
  generatedAt?: string | null;
  /** ISO 8601 UTC timestamp of the last upload of this version's blob to Praetor. Null on unsaved versions. */
  savedToPraetorAt?: string | null;
  /** True when this version is a pre-approval preview (not yet committed). */
  isPending?: boolean;
  /** Direct URL for the pending version's PDF (bypasses /api/documents/{id}/download). */
  previewPdfUrl?: string;
  /** Direct URL for the pending version's diff HTML (bypasses /api/documents/{id}/diff). */
  previewDiffHtmlUrl?: string;
};

/** Shape of one item from GET /api/documents. Mirrors GeneratedDocumentDto on the backend. */
type GeneratedDocumentApiDto = {
  documentId: string;
  order: number;
  title?: string | null;
  description?: string | null;
  suggestedFileName?: string | null;
  currentVersionId?: string | null;
  praetorDocumentId?: string | null;
  praetorLinkAlive?: boolean;
  versions?: DocVersion[];
};

export type GeneratedDocEntry = {
  documentId: string;
  order: number;
  title?: string;
  description?: string;
  suggestedFileName?: string;
  /** Computed from `currentVersionId` on hydration; "rendering" while a turn is in flight. */
  status: "rendering" | "ready" | "error";
  error?: string;
  /** Set once the document has been saved to a Praetor case. Null until then. */
  praetorDocumentId?: string;
  /**
   * Server-resolved aliveness signal for `praetorDocumentId`. True iff the linked Praetor
   * document is still listed in the owning case at hydration time. Drives the Save/Revise
   * button: live link ⇒ "Add new revision", dead link or no id ⇒ "Save to Praetor"
   * (and a successful save self-heals by rewriting `praetorDocumentId` + setting this true).
   */
  praetorLinkAlive?: boolean;
  // Versioning fields — populated from /api/documents response. selectedVersionId tracks
  // the stepper's current selection; defaults to currentVersionId (latest). Auto-advances
  // to the new version on every setReady event so the user sees the freshest version.
  versions: DocVersion[];
  currentVersionId?: string;
  selectedVersionId?: string;
  /** True while a new version is being rendered for an already-ready document (vN+1 flow). */
  updatingVersion?: boolean;
};

type GeneratedDocumentState = {
  entries: Record<string, GeneratedDocEntry>;

  setRendering: (documentId: string, order: number, title?: string | null) => void;
  setReady: (documentId: string, title?: string | null, versionId?: string | null, versionNumber?: number | null) => void;
  setError: (documentId: string, error: string) => void;
  /**
   * Records that a version of a doc was just saved to Praetor: updates the doc-level
   * praetorDocumentId (Praetor reuses it across revisions) and stamps the per-version
   * savedToPraetorAt with the current client time. Server is the source of truth, but
   * this avoids the round-trip on the common path.
   */
  markVersionSavedToPraetor: (documentId: string, versionId: string, praetorDocumentId: string) => void;
  /** Remove the latest version (undo). If only one version existed, removes the entire entry. */
  removeLatestVersion: (documentId: string, removedVersionId: string, newCurrentVersionId?: string) => void;
  selectVersion: (documentId: string, versionId: string) => void;
  /**
   * Injects a "pending" version into the document's version list — shown in the
   * version stepper before the user approves/rejects the edit. The pending version
   * uses the previewId as its versionId. Auto-advances selectedVersionId.
   */
  addPendingVersion: (documentId: string, previewId: string, previewPdfUrl: string, previewDiffHtmlUrl?: string) => void;
  /** Removes any pending version from the document (on rejection or when setReady replaces it). */
  removePendingVersion: (documentId: string) => void;
  fetchDocuments: (ids: string[]) => Promise<void>;
  clear: () => void;
};

export const useGeneratedDocumentStore = create<GeneratedDocumentState>(
  (set, get) => ({
    entries: {},

    setRendering(documentId, order, title) {
      set((s) => {
        const prior = s.entries[documentId];
        // If the document is already ready (has prior versions), don't reset status —
        // just mark it as having an in-flight update so the existing card keeps the
        // completed view while a new version is being rendered.
        if (prior?.status === "ready") {
          return {
            entries: {
              ...s.entries,
              [documentId]: {
                ...prior,
                order,
                updatingVersion: true,
                ...(title ? { title } : {}),
              },
            },
          };
        }
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...(prior ?? {
                documentId,
                order,
                versions: [],
              }),
              order,
              status: "rendering",
              ...(title ? { title } : {}),
            },
          },
        };
      });
    },

    setReady(documentId, title, versionId, versionNumber) {
      set((s) => {
        const prior = s.entries[documentId];
        // Append the new version row to the entry's versions[] (or upsert if it already
        // exists). Auto-advance selectedVersionId + currentVersionId to the new version
        // so the toolbar stepper jumps to the freshest one.
        const versions = prior?.versions ?? [];
        // Strip any pending version — the real committed version replaces it.
        const withoutPending = versions.filter((v) => !v.isPending);
        let nextVersions = withoutPending;
        if (versionId) {
          const idx = withoutPending.findIndex((v) => v.versionId === versionId);
          // hasDiff is conservatively false here — a definitive value lands when the
          // /api/documents fetch fires after the turn (see fetchDocuments below). vN+1
          // produces a diff but the placeholder doesn't know that without a round-trip.
          const placeholder: DocVersion = {
            versionId,
            versionNumber: versionNumber ?? (withoutPending.length + 1),
            hasDiff: false,
            generatedAt: new Date().toISOString(),
          };
          nextVersions = idx >= 0 ? withoutPending.with(idx, { ...withoutPending[idx] }) : [...withoutPending, placeholder];
        }
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...(prior ?? {
                documentId,
                order: 0,
                versions: [],
              }),
              status: "ready",
              updatingVersion: false,
              ...(title ? { title } : {}),
              versions: nextVersions,
              ...(versionId ? { currentVersionId: versionId, selectedVersionId: versionId } : {}),
            },
          },
        };
      });
    },

    setError(documentId, error) {
      set((s) => {
        const prior = s.entries[documentId];
        if (!prior) {
          // SSE-ordering anomaly: a Document_Error event arrived for a document we never
          // saw start rendering (reconnect that skipped the Rendering event, or a
          // server-side pre-rendering validation failure). The defensive fallbacks below
          // construct a structurally complete entry so downstream consumers (versions[]
          // readers in SaveOrReviseButton, GeneratedDocControls, PdfDiffView,
          // ThreadDocumentsPanel) can't dereference undefined.
          ClientLogger.warn("setError called with no prior entry — SSE ordering anomaly", {
            component: "generatedDocumentStore",
            metadata: { documentId },
          });
        }
        // Ordering note: if prior ever produced versions: undefined (today unreachable —
        // setRendering/setReady both seed []), this spread would overwrite the [] fallback.
        // The Bug B optional-chains at the call sites are the secondary defense.
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...prior,
              documentId,
              order: prior?.order ?? 0,
              versions: prior?.versions ?? [],
              status: "error",
              error,
              updatingVersion: false,
            },
          },
        };
      });
    },

    markVersionSavedToPraetor(documentId, versionId, praetorDocumentId) {
      set((s) => {
        const prior = s.entries[documentId];
        if (!prior) return s;
        const now = new Date().toISOString();
        const nextVersions = prior.versions.map((v) =>
          v.versionId === versionId ? { ...v, savedToPraetorAt: now } : v
        );
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...prior,
              praetorDocumentId,
              // Any successful save proves the Praetor link is live now: either we just
              // created the doc, or the stale-link save-fallback rewrote the id to a fresh
              // one. The next render reads this and shows "Add new revision" without
              // waiting for the next hydration round-trip.
              praetorLinkAlive: true,
              versions: nextVersions,
            },
          },
        };
      });
    },

    selectVersion(documentId, versionId) {
      set((s) => {
        const prior = s.entries[documentId];
        if (!prior) return s;
        if (!prior.versions.some((v) => v.versionId === versionId)) return s;
        return {
          entries: {
            ...s.entries,
            [documentId]: { ...prior, selectedVersionId: versionId },
          },
        };
      });
    },

    addPendingVersion(documentId, previewId, previewPdfUrl, previewDiffHtmlUrl) {
      set((s) => {
        const prior = s.entries[documentId];
        if (!prior) return s;
        // Don't add duplicates.
        if (prior.versions.some((v) => v.versionId === previewId)) return s;
        const committedVersions = prior.versions.filter((v) => !v.isPending);
        const nextNumber = (committedVersions.at(-1)?.versionNumber ?? 0) + 1;
        const pendingVersion: DocVersion = {
          versionId: previewId,
          versionNumber: nextNumber,
          hasDiff: !!previewDiffHtmlUrl,
          generatedAt: new Date().toISOString(),
          isPending: true,
          previewPdfUrl,
          previewDiffHtmlUrl: previewDiffHtmlUrl ?? undefined,
        };
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...prior,
              versions: [...committedVersions, pendingVersion],
              selectedVersionId: previewId,
            },
          },
        };
      });
    },

    removePendingVersion(documentId) {
      set((s) => {
        const prior = s.entries[documentId];
        if (!prior) return s;
        const withoutPending = prior.versions.filter((v) => !v.isPending);
        if (withoutPending.length === prior.versions.length) return s;
        // Revert selectedVersionId to the current committed version.
        const fallbackVersionId = prior.currentVersionId ?? withoutPending.at(-1)?.versionId;
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...prior,
              versions: withoutPending,
              selectedVersionId: fallbackVersionId,
            },
          },
        };
      });
    },

    removeLatestVersion(documentId, removedVersionId, newCurrentVersionId) {
      set((s) => {
        const prior = s.entries[documentId];
        if (!prior) return s;
        const nextVersions = prior.versions.filter((v) => v.versionId !== removedVersionId);
        if (nextVersions.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [documentId]: _, ...rest } = s.entries;
          return { entries: rest };
        }
        return {
          entries: {
            ...s.entries,
            [documentId]: {
              ...prior,
              versions: nextVersions,
              currentVersionId: newCurrentVersionId ?? nextVersions[nextVersions.length - 1]?.versionId,
              selectedVersionId: newCurrentVersionId ?? nextVersions[nextVersions.length - 1]?.versionId,
            },
          },
        };
      });
    },

    async fetchDocuments(ids) {
      if (ids.length === 0) return;

      try {
        const res = await apiAuthFetch(
          `/api/documents?ids=${ids.join(",")}`
        );
        if (!res.ok) {
          ClientLogger.warn("Document fetch returned error", {
            action: "/api/documents",
            component: "generatedDocumentStore",
            metadata: { status: res.status, ids },
          });
          return;
        }

        const docs = (await res.json()) as GeneratedDocumentApiDto[];

        const newEntries: Record<string, GeneratedDocEntry> = {};
        for (const doc of docs) {
          // Status is derived from the row's own state: a doc with a current version is
          // serviceable by definition (the blob's existence is verified by the download
          // endpoint at fetch time, not here). No current version → no useful download
          // path → flag as error so the card surfaces it instead of spinning forever.
          const status = doc.currentVersionId ? "ready" : "error";
          const versions = (doc.versions ?? []).slice().sort((a, b) => a.versionNumber - b.versionNumber);
          newEntries[doc.documentId] = {
            documentId: doc.documentId,
            order: doc.order,
            title: doc.title ?? undefined,
            description: doc.description ?? undefined,
            suggestedFileName: doc.suggestedFileName ?? undefined,
            status,
            praetorDocumentId: doc.praetorDocumentId ?? undefined,
            praetorLinkAlive: doc.praetorLinkAlive,
            versions,
            currentVersionId: doc.currentVersionId ?? undefined,
            selectedVersionId: doc.currentVersionId ?? undefined,
          };
        }

        set((s) => ({
          entries: { ...s.entries, ...newEntries },
        }));
      } catch (err) {
        ClientLogger.error("Failed to fetch generated documents", {
          error: err,
          component: "generatedDocumentStore",
        });
      }
    },

    clear() {
      set({ entries: {} });
    },
  })
);
